import { MouseEvent } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, FormControl, FormHelperText } from "@mui/material"
import { faLaptopMobile } from "@fortawesome/pro-light-svg-icons"
import { MessageChannelCard, ChannelCardConfig } from "./MessageChannelCard"
import { MessageAdditionalDataChannelsType } from "../messagesTypes"

const useStyles = makeStyles()(() => ({
    root: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "2rem",
    },
    helperText: {
        marginTop: ".5rem",
    },
}))

const CHANNEL_OPTIONS: ChannelCardConfig[] = [
    {
        type: MessageAdditionalDataChannelsType.IN_APP,
        icon: faLaptopMobile,
    },
]

type MessageChannelsCardsProps = Readonly<{
    name: string
}>

export function MessageChannelsCards({ name }: MessageChannelsCardsProps) {
    const [field, meta, helpers] = useField<MessageAdditionalDataChannelsType[]>(name)
    const { classes } = useStyles()

    const selectedValues = Array.isArray(field.value) ? field.value : []

    const showError = Boolean(meta.touched && meta.error)
    const errorMessage = showError && typeof meta.error === "string" ? meta.error : undefined

    const enabledOptions = CHANNEL_OPTIONS.filter((o) => !o.disabled)
    const hasSingleEnabledOption = enabledOptions.length === 1
    const singleEnabledType = hasSingleEnabledOption ? enabledOptions[0].type : undefined

    const handleToggle = (channel: MessageAdditionalDataChannelsType) => {
        const alreadySelected = selectedValues.includes(channel)

        if (hasSingleEnabledOption && channel === singleEnabledType && alreadySelected && selectedValues.length === 1) {
            return
        }

        const nextValue = alreadySelected ? selectedValues.filter((c) => c !== channel) : [...selectedValues, channel]

        helpers.setValue(nextValue)
        helpers.setTouched(true, false)
    }

    const handleBlur = (event: MouseEvent<HTMLDivElement>) => {
        field.onBlur(event as any)
    }

    return (
        <FormControl component="fieldset" fullWidth error={showError} variant="standard">
            <Box className={classes.root} onBlur={handleBlur as any}>
                {CHANNEL_OPTIONS.map((option) => (
                    <MessageChannelCard
                        key={option.type}
                        option={option}
                        selected={selectedValues.includes(option.type)}
                        onToggle={handleToggle}
                    />
                ))}
            </Box>

            {errorMessage && <FormHelperText className={classes.helperText}>{errorMessage}</FormHelperText>}
        </FormControl>
    )
}
